// Client port of backend/services/form_rules.py (8B-2). Usability only — the server re-evaluates the pinned version on submit.
// Parity with the shared fixture backend/tests/fixtures/form_rules_cases.json is enforced by lib/__tests__/formRules.test.js.
export const ENGINE_VERSION = 1;
export const SUPPORTED_ENGINE_VERSIONS = new Set([ENGINE_VERSION]);
export const ARRAY_TYPES = new Set(['multi_select', 'checkbox']);
export const NON_DATA_TYPES = new Set(['section_header', 'divider']);
export const SCALAR_OPS = ['equals', 'not_equals', 'in', 'is_empty', 'not_empty'];
export const ARRAY_OPS = ['contains', 'not_contains', 'is_empty', 'not_empty'];
export const ACTIONS = ['show', 'hide', 'require'];
export const MAX_DEPTH = 5;

export const OP_LABELS = { equals: 'is', not_equals: 'is not', in: 'is one of', is_empty: 'is blank', not_empty: 'is answered', contains: 'includes', not_contains: 'does not include' };
export const ACTION_LABELS = { show: 'show', hide: 'hide', require: 'require' };

export function engineActive(version) { return !!version && SUPPORTED_ENGINE_VERSIONS.has(version.rules_engine_version); }
export function engineUnsupported(version) { const v = version?.rules_engine_version; return v != null && !SUPPORTED_ENGINE_VERSIONS.has(v); }
export function canControl(field) { return !!field?.key && !NON_DATA_TYPES.has(field.type) && field.type !== 'signature_drawn'; }
export function canBeTarget(field, action) { return !!field?.key && field.type !== 'signature_drawn' && !(action === 'require' && NON_DATA_TYPES.has(field.type)); }
export function operatorsFor(field) { return ARRAY_TYPES.has(field?.type) ? ARRAY_OPS : SCALAR_OPS; }

const isBlank = (v) => v == null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0);
const norm = (v) => Array.isArray(v) ? v.map(x => String(x).trim()) : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v == null ? null : String(v).trim();
const fieldMap = (schema) => { const m = new Map(); (schema || []).forEach(f => { if (f && f.key) m.set(f.key, f); }); return m; };

function edgesOf(rules) {
  const edges = new Map();
  (rules || []).forEach(r => { const c = r?.when?.field; (r?.then?.fields || []).forEach(k => { if (!edges.has(c)) edges.set(c, new Set()); edges.get(c).add(k); }); });
  return edges;
}

export function topo(edges) {
  const nodes = new Set([...edges.keys()]); edges.forEach(ts => ts.forEach(t => nodes.add(t)));
  const indeg = new Map([...nodes].map(n => [n, 0])); edges.forEach(ts => ts.forEach(t => indeg.set(t, indeg.get(t) + 1)));
  const ready = [...nodes].filter(n => indeg.get(n) === 0).sort(); const order = [];
  while (ready.length) {
    const n = ready.shift(); order.push(n);
    [...(edges.get(n) || [])].sort().forEach(t => { indeg.set(t, indeg.get(t) - 1); if (indeg.get(t) === 0) { ready.push(t); ready.sort(); } });
  }
  return { order, cycle: new Set([...nodes].filter(n => indeg.get(n) > 0)) };
}

export function maxDepth(edges, order) {
  const depth = new Map(order.map(n => [n, 0]));
  order.forEach(n => (edges.get(n) || []).forEach(t => depth.set(t, Math.max(depth.get(t) || 0, depth.get(n) + 1))));
  return Math.max(0, ...depth.values());
}

export function validateRules(schema, rules) {
  const fields = fieldMap(schema); const errors = []; const ids = new Set(); const edges = new Map();
  rules = rules || [];
  if (rules.length > 200) errors.push({ rule_id: null, code: 'RULES_TOO_MANY', message: 'At most 200 rules.' });
  rules.forEach((r, i) => {
    const rid = String(r?.id || `#${i}`); const err = (code, message) => errors.push({ rule_id: rid, code, message });
    const isObj = (x) => x && typeof x === 'object' && !Array.isArray(x);
    if (!isObj(r) || !isObj(r.when) || !isObj(r.then) || Object.keys(r).some(k => !['id', 'when', 'then'].includes(k))) { err('RULE_SHAPE_INVALID', 'Rule must be {id, when{field, op, value}, then{action, fields}}.'); return; }
    if (!r.id || ids.has(rid)) err('RULE_ID_INVALID', 'Rule id must be unique and non-empty.');
    ids.add(rid);
    const w = r.when, t = r.then;
    if (Object.keys(w).some(k => !['field', 'op', 'value'].includes(k)) || Object.keys(t).some(k => !['action', 'fields'].includes(k))) { err('RULE_SHAPE_INVALID', 'Unknown keys in when/then.'); return; }
    const ctl = fields.get(w.field); const op = w.op; const val = w.value;
    if (!ctl) { err('RULE_UNKNOWN_FIELD', `Controlling field '${w.field}' is not in this schema.`); return; }
    if (NON_DATA_TYPES.has(ctl.type) || ctl.type === 'signature_drawn') err('RULE_CONTROLLER_INVALID', `${ctl.type} fields cannot control conditions.`);
    const allowed = operatorsFor(ctl);
    if (!allowed.includes(op)) err('RULE_OPERATOR_INVALID', `Operator '${op}' is not allowed for a ${ctl.type} field.`);
    else if (op === 'is_empty' || op === 'not_empty') { if (val != null) err('RULE_VALUE_INVALID', `${op} takes no value.`); }
    else if (op === 'in') {
      if (!Array.isArray(val) || !val.length || !val.every(x => typeof x === 'string')) err('RULE_VALUE_INVALID', '"in" needs a non-empty list of strings.');
      else if (ctl.options?.length && val.some(x => !ctl.options.includes(x))) err('RULE_VALUE_INVALID', 'Values must be among the field options.');
    } else {
      if (typeof val !== 'string' || !val.trim()) err('RULE_VALUE_INVALID', `${op} needs a non-empty string value.`);
      else if (ctl.options?.length && !ctl.options.includes(val)) err('RULE_VALUE_INVALID', 'Value must be one of the field options.');
    }
    if (!ACTIONS.includes(t.action)) err('RULE_ACTION_INVALID', `Action '${t.action}' is not allowed.`);
    const tf = t.fields;
    if (!Array.isArray(tf) || !tf.length || tf.length > 50 || new Set(tf).size !== tf.length) { err('RULE_TARGETS_INVALID', 'then.fields must be a non-empty list of unique keys (max 50).'); return; }
    tf.forEach(k => {
      const tgt = fields.get(k);
      if (!tgt) { err('RULE_UNKNOWN_FIELD', `Target field '${k}' is not in this schema.`); return; }
      if (k === w.field) err('RULE_SELF_REFERENCE', 'A rule cannot target its own controlling field.');
      if (tgt.type === 'signature_drawn') err('RULE_TARGET_INVALID', 'signature_drawn fields cannot be hidden or conditionally required.');
      if (t.action === 'require' && NON_DATA_TYPES.has(tgt.type)) err('RULE_TARGET_INVALID', `${tgt.type} cannot be required.`);
      if (!edges.has(w.field)) edges.set(w.field, new Set()); edges.get(w.field).add(k);
    });
  });
  if (!errors.length) {
    const { order, cycle } = topo(edges);
    if (cycle.size) errors.push({ rule_id: null, code: 'RULE_CYCLE', message: `Conditions form a cycle through: ${[...cycle].sort().join(', ')}.` });
    else if (maxDepth(edges, order) > MAX_DEPTH) errors.push({ rule_id: null, code: 'RULE_DEPTH_EXCEEDED', message: `Condition chains deeper than ${MAX_DEPTH} are not allowed.` });
  }
  return errors;
}

function fires(op, ctlValue, val, isArray) {
  const v = norm(ctlValue); const blank = isBlank(v);
  if (op === 'is_empty') return blank;
  if (op === 'not_empty') return !blank;
  if (isArray) {
    const arr = Array.isArray(v) ? v : (blank ? [] : [v]);
    if (op === 'contains') return arr.includes(String(val).trim());
    if (op === 'not_contains') return !arr.includes(String(val).trim());
    return false;
  }
  const s = blank ? null : (typeof v === 'string' ? v : null);
  if (op === 'equals') return s != null && s === String(val).trim();
  if (op === 'not_equals') return s == null || s !== String(val).trim();
  if (op === 'in') return s != null && (val || []).map(x => String(x).trim()).includes(s);
  return false;
}

export function resolveRules(schema, rules, data) {
  const fields = fieldMap(schema); rules = rules || []; data = data || {};
  const { order } = topo(edgesOf(rules)); const pos = new Map(order.map((k, i) => [k, i]));
  const hidden = new Set(), shown = new Set(), showTargets = new Set(), fired = [];
  const required = new Set([...fields.values()].filter(f => f.required).map(f => f.key));
  const sorted = [...rules].sort((a, b) => { const pa = pos.get(a.when.field) ?? -1, pb = pos.get(b.when.field) ?? -1; return pa !== pb ? pa - pb : String(a.id) < String(b.id) ? -1 : String(a.id) > String(b.id) ? 1 : 0; });
  sorted.forEach(r => {
    const ctlKey = r.when.field; const ctl = fields.get(ctlKey); const act = r.then.action;
    if (act === 'show') r.then.fields.forEach(k => showTargets.add(k));
    const ctlHidden = hidden.has(ctlKey) || (showTargets.has(ctlKey) && !shown.has(ctlKey));
    if (ctlHidden) return;
    if (!fires(r.when.op, data[ctlKey], r.when.value, ARRAY_TYPES.has(ctl?.type))) return;
    fired.push(String(r.id));
    if (act === 'show') r.then.fields.forEach(k => shown.add(k));
    else if (act === 'hide') r.then.fields.forEach(k => hidden.add(k));
    else if (act === 'require') r.then.fields.forEach(k => required.add(k));
  });
  showTargets.forEach(k => { if (!shown.has(k)) hidden.add(k); });
  hidden.forEach(k => required.delete(k));
  fields.forEach(f => { if (NON_DATA_TYPES.has(f.type)) required.delete(f.key); });
  return { hidden, required, fired };
}

export function validateSubmission(schema, rules, data, skipKeys = new Set()) {
  const fields = new Map([...fieldMap(schema)].filter(([, f]) => !NON_DATA_TYPES.has(f.type)));
  const res = resolveRules(schema, rules, data); const errors = []; data = data || {};
  Object.keys(data).filter(k => !fields.has(k)).sort().forEach(k => errors.push({ field: k, code: 'UNKNOWN_FIELD', message: `'${k}' is not a field of this form version.` }));
  [...res.required].sort().forEach(k => { if (!skipKeys.has(k) && isBlank(data[k])) errors.push({ field: k, code: 'REQUIRED_MISSING', message: `${fields.get(k)?.label || k} is required.` }); });
  Object.entries(data).forEach(([k, v]) => {
    const f = fields.get(k);
    if (!f || res.hidden.has(k) || skipKeys.has(k) || isBlank(v)) return;
    const opts = f.options; const label = f.label || k;
    if (ARRAY_TYPES.has(f.type)) { if (!Array.isArray(v) || (opts?.length && norm(v).some(x => !opts.includes(x)))) errors.push({ field: k, code: 'TYPE_INVALID', message: `${label}: value must be a list of allowed options.` }); }
    else if ((f.type === 'dropdown' || f.type === 'radio') && opts?.length && !opts.includes(norm(v))) errors.push({ field: k, code: 'TYPE_INVALID', message: `${label}: value is not an allowed option.` });
    else if (f.type === 'yes_no' && !['Yes', 'No'].includes(norm(v))) errors.push({ field: k, code: 'TYPE_INVALID', message: `${label}: value must be Yes or No.` });
    else if (v !== null && typeof v === 'object') errors.push({ field: k, code: 'TYPE_INVALID', message: `${label}: unexpected structured value.` });
  });
  const hiddenKeys = [...res.hidden].filter(k => k in data).sort();
  const stripped = Object.fromEntries(Object.entries(data).filter(([k]) => !res.hidden.has(k)));
  return { errors, stripped, hiddenKeys, hidden: res.hidden, required: res.required };
}

// ── Builder helpers ─────────────────────────────────────────────────────────
export function describeRule(rule, schema) {
  const fields = fieldMap(schema); const name = (k) => fields.get(k)?.label || k;
  const w = rule?.when || {}, t = rule?.then || {};
  const val = w.op === 'is_empty' || w.op === 'not_empty' ? '' : Array.isArray(w.value) ? ` ${w.value.map(v => `“${v}”`).join(' or ')}` : ` “${w.value ?? ''}”`;
  return `When ${name(w.field)} ${OP_LABELS[w.op] || w.op}${val}, ${ACTION_LABELS[t.action] || t.action} ${(t.fields || []).map(name).join(', ')}.`;
}
export function dependentRules(rules, key) { return (rules || []).filter(r => r?.when?.field === key || (r?.then?.fields || []).includes(key)); }
export function renameKeyInRules(rules, oldKey, newKey) {
  if (oldKey == null || oldKey === newKey) return rules || [];
  return (rules || []).map(r => ({ ...r, when: { ...r.when, field: r.when.field === oldKey ? newKey : r.when.field }, then: { ...r.then, fields: r.then.fields.map(k => k === oldKey ? newKey : k) } }));
}
export function removeKeyFromRules(rules, key) {
  return (rules || []).filter(r => r.when.field !== key).map(r => ({ ...r, then: { ...r.then, fields: r.then.fields.filter(k => k !== key) } })).filter(r => r.then.fields.length > 0);
}
export function nextRuleId(rules) { let n = (rules || []).length + 1; const ids = new Set((rules || []).map(r => r.id)); while (ids.has(`r${n}`)) n += 1; return `r${n}`; }
